import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons"
import Loader from "../basics/Loader"
import { useGroups } from "../../hooks/groups"
import { fetchDepEtat } from "../../store/actions/depannageActions"
import { setFeuilletage } from "../../store/actions/feuilletageActions"
import { filterEmployesGroupe } from "../../utilities/employes"
import { COLORS } from "../../utilities/Constants"
import { generalStyles } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"

// Page /depannage/etat (GE-46), calquée sur AtelierEtatV2. Le backend
// (DepannageEtatView) reste la SOURCE DE VÉRITÉ : même gating par groupe +
// 403, et dépanneur FORCÉ à soi pour un employé. ⚠️ Miroir EXACT de
// DEPANNAGE_STATE_MENUS (backend/svp/depannage_suivi.py).
const ETAT_MENUS = [
	{
		key: "mesDepannages",
		label: "Mes dépannages",
		groups: ["employe", "responsable_depannage", "admin_depannage"],
	},
	{
		key: "aCompleter",
		label: "À compléter",
		groups: ["employe", "responsable_depannage", "admin_depannage"],
	},
]

// Voient tous les dépanneurs (sélecteur) — boss contourne via hasGroup.
const GROUPES_RESPONSABLES = ["responsable_depannage", "admin_depannage"]

const formatMinutes = (min) => {
	if (min === null || min === undefined) return "—"
	if (min < 60) return `${min} min`
	const h = Math.floor(min / 60)
	const m = min % 60
	return `${h} h ${String(m).padStart(2, "0")}`
}

const StatCard = ({ label, value, sub }) => (
	<div style={generalStyles.statCard}>
		<span style={generalStyles.statLabel}>{label}</span>
		<span style={generalStyles.statValue}>{value}</span>
		{sub && <span style={generalStyles.statSub}>{sub}</span>}
	</div>
)

const repartitionTexte = (r) =>
	r
		? `Police ${r.police} · Assistance ${r.assistance} · Privé ${r.prive}`
		: ""

const StatsCartes = ({ stats }) => {
	const { jour, semaine } = stats
	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
				gap: 12,
				marginBottom: 24,
			}}
		>
			<StatCard
				label="Missions du jour"
				value={jour.nb}
				sub={repartitionTexte(jour.repartition)}
			/>
			<StatCard
				label="Missions de la semaine"
				value={semaine.nb}
				sub={repartitionTexte(semaine.repartition)}
			/>
			<StatCard
				label="Délai moyen d'arrivée"
				value={formatMinutes(semaine.delai_arrivee_min)}
				sub={`Aujourd'hui : ${formatMinutes(jour.delai_arrivee_min)}`}
			/>
			<StatCard
				label="Temps moyen sur place"
				value={formatMinutes(semaine.sur_place_min)}
				sub={`Aujourd'hui : ${formatMinutes(jour.sur_place_min)}`}
			/>
			<StatCard
				label="Temps moyen de mission"
				value={formatMinutes(semaine.mission_min)}
				sub={`Aujourd'hui : ${formatMinutes(jour.mission_min)}`}
			/>
		</div>
	)
}

const Ligne = ({ index, onOpen, children }) => {
	const [hover, setHover] = useState(false)
	return (
		<tr
			onDoubleClick={onOpen}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				cursor: onOpen ? "pointer" : "default",
				backgroundColor: hover
					? tableStyles.rowHover
					: tableStyles.rowBg(index),
			}}
		>
			{children}
		</tr>
	)
}

const MissionsDuJour = ({ missions, avecDepanneur, openFiche }) => {
	if (missions.length === 0)
		return (
			<div style={tableStyles.empty}>
				Aucune mission démarrée aujourd'hui
			</div>
		)
	return (
		<div style={tableStyles.container}>
			<table style={tableStyles.table}>
				<thead>
					<tr>
						<th style={tableStyles.th}>Départ</th>
						<th style={tableStyles.th}>Arrivée</th>
						<th style={tableStyles.th}>Fin</th>
						<th style={tableStyles.th}>Appel</th>
						{avecDepanneur && (
							<th style={tableStyles.th}>Dépanneur</th>
						)}
						<th style={tableStyles.th}>Plaques</th>
						<th style={tableStyles.th}>Lieu</th>
						<th style={tableStyles.th}>Destination</th>
						<th style={tableStyles.th}>Etat</th>
					</tr>
				</thead>
				<tbody>
					{missions.map((m, i) => (
						<Ligne
							key={m.id}
							index={i}
							onOpen={m.dep_id ? () => openFiche(m.dep_id) : null}
						>
							<td style={tableStyles.td}>{m.t0 || "—"}</td>
							<td style={tableStyles.td}>{m.t1 || "—"}</td>
							<td style={tableStyles.td}>
								{m.t3 || m.t2 || "—"}
							</td>
							<td style={tableStyles.td}>{m.appel_label}</td>
							{avecDepanneur && (
								<td style={tableStyles.td}>
									{m.depanneur_nom}
								</td>
							)}
							<td style={tableStyles.td}>{m.plaques}</td>
							<td style={tableStyles.td}>{m.lieu}</td>
							<td style={tableStyles.td}>{m.destination}</td>
							<td style={tableStyles.td}>
								{m.dep_id
									? `Fiche n° ${m.dep_id}`
									: m.etat_label}
							</td>
						</Ligne>
					))}
				</tbody>
			</table>
		</div>
	)
}

const FichesACompleter = ({ fiches, avecDepanneur, openFiche }) => {
	if (fiches.length === 0)
		return <div style={tableStyles.empty}>Aucune fiche à compléter</div>
	return (
		<div style={tableStyles.container}>
			<table style={tableStyles.table}>
				<thead>
					<tr>
						<th style={{ ...tableStyles.th, width: 64 }}>N°</th>
						<th style={tableStyles.th}>Date</th>
						<th style={tableStyles.th}>Appel</th>
						{avecDepanneur && (
							<th style={tableStyles.th}>Dépanneur</th>
						)}
						<th style={tableStyles.th}>Client</th>
						<th style={tableStyles.th}>Plaques</th>
						<th style={tableStyles.th}>Destination</th>
						<th style={tableStyles.th}>Champs manquants</th>
					</tr>
				</thead>
				<tbody>
					{fiches.map((f, i) => (
						<Ligne
							key={f.id}
							index={i}
							onOpen={() => openFiche(f.id)}
						>
							<td style={tableStyles.td}>{f.id}</td>
							<td style={tableStyles.td}>{f.date || "—"}</td>
							<td style={tableStyles.td}>{f.appel_label}</td>
							{avecDepanneur && (
								<td style={tableStyles.td}>
									{f.depanneur_nom}
								</td>
							)}
							<td style={tableStyles.td}>{f.client}</td>
							<td style={tableStyles.td}>{f.plaques}</td>
							<td style={tableStyles.td}>{f.destination}</td>
							<td
								style={{
									...tableStyles.td,
									color: COLORS.warning,
									whiteSpace: "normal",
								}}
							>
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									style={{ marginRight: 8 }}
								/>
								{f.champs_manquants
									.map((c) => c.libelle)
									.join(", ")}
							</td>
						</Ligne>
					))}
				</tbody>
			</table>
		</div>
	)
}

const DepEtatV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { hasGroup } = useGroups()
	const { employeId } = useSelector((state) => state.navInfo.login)
	const { employes } = useSelector((state) => state.masterReducer)
	const { depEtat, depACompleterCount } = useSelector(
		(state) => state.depInfo,
	)
	const responsable = hasGroup(GROUPES_RESPONSABLES)

	const [menu, setMenu] = useState("mesDepannages")
	// 0 = tous. Responsable : soi-même sur « Mes dépannages », tous sur « À
	// compléter » (décision 02.10) ; employé : ignoré (forcé côté serveur).
	const [depanneurId, setDepanneurId] = useState(
		responsable ? employeId || 0 : 0,
	)

	const visibleMenus = useMemo(
		() =>
			ETAT_MENUS.filter(
				(m) =>
					hasGroup(m.groups) &&
					// « À compléter (n) » masqué tant qu'il n'y a rien.
					(m.key !== "aCompleter" ||
						depACompleterCount > 0 ||
						menu === "aCompleter"),
			),
		[hasGroup, depACompleterCount, menu],
	)

	const depanneurs = useMemo(
		() => filterEmployesGroupe(employes, depanneurId),
		[employes, depanneurId],
	)

	useEffect(() => {
		if (visibleMenus.some((m) => m.key === menu))
			dispatch(fetchDepEtat(menu, responsable ? depanneurId : 0))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menu, depanneurId])

	const clickMenu = (key) => {
		if (key === menu) return
		if (responsable && key === "aCompleter") setDepanneurId(0)
		setMenu(key)
	}

	const data = depEtat.menu === menu ? depEtat.data : null
	const fiches = (data && data.results) || []

	const openFiche = (id) => {
		if (menu === "aCompleter")
			dispatch(
				setFeuilletage(
					"dep",
					fiches.map((f) => f.id),
					"depEtat",
				),
			)
		history.push(`/depannage/fiche/${id}`)
	}

	if (visibleMenus.length === 0)
		return (
			<div style={generalStyles.page}>
				<div style={tableStyles.empty}>
					Aucun menu disponible pour votre profil.
				</div>
			</div>
		)

	const avecDepanneur = responsable && !depanneurId

	return (
		<div style={generalStyles.page}>
			<div style={{ maxWidth: 1400, margin: "0 auto" }}>
				<h2
					style={{
						...generalStyles.title,
						textAlign: "left",
						marginBottom: 24,
					}}
				>
					Etat du Dépannage
				</h2>

				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 16,
						flexWrap: "wrap",
					}}
				>
					<div style={generalStyles.segmented}>
						{visibleMenus.map((m) => (
							<button
								key={m.key}
								type="button"
								onClick={() => clickMenu(m.key)}
								style={generalStyles.segment(menu === m.key)}
							>
								{m.key === "aCompleter"
									? `${m.label} (${
											menu === "aCompleter" && data
												? fiches.length
												: depACompleterCount
										})`
									: m.label}
							</button>
						))}
					</div>
					{responsable && (
						<select
							aria-label="Dépanneur"
							value={depanneurId}
							onChange={(e) =>
								setDepanneurId(parseInt(e.target.value) || 0)
							}
							style={{
								...generalStyles.select,
								width: 240,
								flex: "0 0 auto",
							}}
						>
							<option value={0}>Tous les dépanneurs</option>
							{depanneurs.map((e) => (
								<option key={e.id} value={e.id}>
									{e.nom}
								</option>
							))}
						</select>
					)}
				</div>

				{!data && depEtat.error && !depEtat.loading ? (
					<div style={tableStyles.empty}>
						Impossible de charger les données.
					</div>
				) : !data ? (
					// Chargement OU changement de menu pas encore parti (le
					// fetch suit le rendu) : jamais de faux message d'erreur.
					<Loader variant="ring" message="Chargement…" />
				) : menu === "mesDepannages" ? (
					<>
						<StatsCartes stats={data.stats} />
						<div
							style={{
								...generalStyles.cardTitle,
								marginBottom: 12,
							}}
						>
							Dépannages du jour
						</div>
						<MissionsDuJour
							missions={data.missions_du_jour}
							avecDepanneur={avecDepanneur}
							openFiche={openFiche}
						/>
					</>
				) : (
					<FichesACompleter
						fiches={fiches}
						avecDepanneur={avecDepanneur}
						openFiche={openFiche}
					/>
				)}
			</div>
		</div>
	)
}

export default DepEtatV2
